import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Anchor } from "@tento-ui/components/anchor";
import { ContextMenu } from "@tento-ui/components/context-menu";
import { DatePickerModal } from "@tento-ui/components/input/date-picker-modal";
import { DomUtils } from "@tento-ui/dom";
import { Typography } from "@tento-ui/ui/typography";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";

@component({
  name: "task-date-display",
})
export class TaskDateDisplay extends LonaWebComponent {
  private showOverdue: boolean = true;

  bind(date?: Option<any>, options?: { showOverdue?: boolean }) {
    this.showOverdue = options?.showOverdue ?? true;

    // Clear existing content in shadow DOM
    DomUtils.clearChildren(this.$("container"));

    if (!date) {
      this.removeAttribute("has-date");
      this.removeAttribute("is-past");
      this.removeAttribute("is-today");
      const $emptyDate = Typography.caption("");
      this.$("container").appendChild($emptyDate);
      return;
    }

    this.setAttribute("has-date", "");

    // Parse the date - handle both string and object formats
    const dateObj =
      typeof date === "string" ? new Date(date) : new Date(date.toString());

    let displayText: string;
    if (Number.isNaN(dateObj.getTime())) {
      displayText = "Invalid date";
    } else {
      // Format the date
      const formatted = this.formatDate(dateObj);
      displayText = formatted.display;

      // Set attributes for styling based on date status
      this.toggleAttribute("is-today", formatted.isToday);
      this.toggleAttribute("is-past", formatted.isPast && this.showOverdue);

      // Set tooltip with full date
      this.setAttribute(
        "title",
        dateObj.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      );
    }

    const $date = Typography.caption(displayText);
    this.$("container").appendChild($date);
  }

  private formatDate(date: Date): {
    display: string;
    isToday: boolean;
    isPast: boolean;
  } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    const isToday = dateOnly.getTime() === today.getTime();
    const isPast = date.getTime() < now.getTime();

    let display: string;

    if (isToday) {
      display = "Today";
    } else {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (dateOnly.getTime() === tomorrow.getTime()) {
        display = "Tomorrow";
      } else if (dateOnly.getTime() === yesterday.getTime()) {
        display = "Yesterday";
      } else {
        // Calculate days difference
        const daysDiff = Math.floor(
          (dateOnly.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
        );

        if (daysDiff > 0 && daysDiff <= 7) {
          // Within a week - show day name
          display = date.toLocaleDateString("en-US", { weekday: "short" });
        } else if (date.getFullYear() === now.getFullYear()) {
          // Same year - show month and day
          display = date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        } else {
          // Different year - show full date
          display = date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
        }
      }
    }

    return { display, isToday, isPast };
  }

  static $styles = [
    css`
      :host {
        display: flex;
        min-height: 24px;
        --font-size: var(--caption-size);
        color: var(--text-sec);
        min-width: 80px;
        cursor: pointer;
        border-radius: 4px;
        padding-inline: 4px;
        transition: background-color 0.2s ease;
      }

      :host([inline]) {
        color: var(--text-ter);
      }

      :host([inline]:not([has-date])) {
        display: none;
      }

      :host(:hover) {
        background-color: var(--bg-hover);
      }

      :host([is-today]) {
        color: var(--primary-color, #2196f3);
        font-weight: 500;
      }

      :host([is-past]:not([is-today])) {
        color: var(--error-color, #f44336);
      }

      #container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        flex: 1;
        white-space: nowrap;
      }
    `,
  ];

  static $html = html`
    <div id="container"></div>
  `;
}

export namespace TaskDateDisplay {
  export type DateMenuId = "today" | "tomorrow" | "pick" | "clear";

  export const MENU_ITEMS: Anchor.MenuItem<DateMenuId>[] = [
    { id: "today", label: "Set to Today" },
    { id: "tomorrow", label: "Set to Tomorrow" },
    { id: "pick", label: "Pick Date..." },
    { id: "clear", label: "Clear Date" },
  ];

  export function buildMenu(
    onSelect: (id: DateMenuId) => void,
  ): ContextMenu.Built {
    const builder = ContextMenu.builder();
    for (const item of MENU_ITEMS) {
      builder.cellRow(item.label, null, (state) => {
        onSelect(item.id);
        state.shouldDismiss = true;
      });
    }
    return builder.build({ onDismiss: () => {} });
  }

  export function dateForMenuItem(menuId: DateMenuId): Option<DateTime<Utc>> {
    switch (menuId) {
      case "today":
        return DateTime.utc.now();
      case "tomorrow":
        return DateTime.utc.now().add({ days: 1 });
      case "clear":
        return null;
      default:
        return null;
    }
  }

  export function applyMenuItem($display: TaskDateDisplay, menuId: DateMenuId) {
    switch (menuId) {
      case "today":
        $display.bind(DateTime.utc.now());
        break;
      case "tomorrow":
        $display.bind(DateTime.utc.now().add({ days: 1 }));
        break;
      case "clear":
        $display.bind(undefined);
        break;
    }
  }

  export function showPickerModal(
    currentDate: Option<NaiveDate>,
    onDateSelected: (date: Option<NaiveDate>) => void,
    title: string = "Select Due Date",
  ) {
    DatePickerModal.show(title, currentDate, onDateSelected);
  }
}
